import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { MapPin, ChevronRight, Navigation } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { t } from "@/i18n";

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom marker icon
const customIcon = new L.DivIcon({
  className: "custom-marker-container",
  html: `<div class="custom-marker"><div class="w-2 h-2 bg-white rounded-full"></div></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Component to fit map bounds to all markers
const FitBounds = ({ locations }) => {
  const map = useMap();
  
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(loc => [loc.lat, loc.long]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [locations, map]);
  
  return null;
};

export const LocationsPage = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await axios.get(`${API}/locations`);
        setLocations(res.data);
        if (res.data.length > 0) {
          setSelectedLocation(res.data[0]);
        }
      } catch (err) {
        console.error("Error fetching locations:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, []);

  const defaultCenter = [37.7749, -122.4194];
  const center = locations.length > 0 
    ? [locations[0].lat, locations[0].long] 
    : defaultCenter;

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-b border-gray-100">
        <h1 className="text-2xl font-bold text-[#111827]">{t('locations.title')}</h1>
        <p className="text-[#6b7280] text-sm mt-1">{t('locations.subtitle')}</p>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-180px)]">
        {/* Locations List */}
        <div className="w-full lg:w-[350px] overflow-y-auto border-r border-gray-100">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 h-20 rounded-xl" />
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 space-y-2">
              {locations.map((location) => (
                <div
                  key={location.id}
                  data-testid={`location-card-${location.id}`}
                  className={`location-card p-4 rounded-xl cursor-pointer border-2 transition-colors ${
                    selectedLocation?.id === location.id
                      ? "border-[#1a4d2e] bg-[#f2f4f3]"
                      : "border-transparent"
                  }`}
                  onClick={() => setSelectedLocation(location)}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#1a4d2e]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-[#1a4d2e]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#111827] truncate">
                        {location.name}
                      </h3>
                      <p className="text-sm text-[#6b7280] truncate">
                        {location.address}
                      </p>
                      <p className="mono text-xs text-[#9ca3af] mt-1">
                        {location.lat.toFixed(4)}, {location.long.toFixed(4)}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-[#9ca3af] flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Map */}
        <div className="flex-1 relative">
          {!loading && (
            <MapContainer
              center={center}
              zoom={4}
              className="w-full h-full"
              style={{ minHeight: "400px" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitBounds locations={locations} />
              {locations.map((location) => (
                <Marker
                  key={location.id}
                  position={[location.lat, location.long]}
                  icon={customIcon}
                  eventHandlers={{
                    click: () => setSelectedLocation(location),
                  }}
                >
                  <Popup>
                    <div className="p-2">
                      <h3 className="font-bold text-[#111827]">{location.name}</h3>
                      <p className="text-sm text-[#6b7280] mb-3">{location.address}</p>
                      <button
                        data-testid={`view-products-btn-${location.id}`}
                        onClick={() => navigate(`/locations/${location.id}`)}
                        className="btn-primary w-full py-2 rounded-lg text-sm font-medium"
                      >
                        {t('locations.viewProducts')}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          )}

          {/* Selected Location Card (Mobile) */}
          {selectedLocation && (
            <div className="absolute bottom-4 left-4 right-4 lg:hidden bg-white rounded-xl shadow-lg p-4 border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-[#1a4d2e] flex items-center justify-center">
                  <Navigation className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-[#111827]">{selectedLocation.name}</h3>
                  <p className="text-sm text-[#6b7280]">{selectedLocation.address}</p>
                </div>
              </div>
              <button
                data-testid="view-products-mobile-btn"
                onClick={() => navigate(`/locations/${selectedLocation.id}`)}
                className="btn-primary w-full py-3 rounded-xl font-semibold text-sm"
              >
                {t('locations.viewProducts')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
